import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateTimeFieldComponent } from '../date-time-field/date-time-field.component';

@Component({
  selector: 'lib-devices-lifecycle-event-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DateTimeFieldComponent],
  template: `
    <form [formGroup]="form" (ngSubmit)="submitForm.emit()">
      <div class="devices-lifecycle-event-form__grid">
        <label>
          {{ eventTypeLabel }}
          <select formControlName="eventType">
            @for (type of lifecycleTypes; track type) {
              <option [value]="type">{{ type }}</option>
            }
          </select>
        </label>

        <label>
          {{ performedAtLabel }}
          <lib-date-time-field inputId="devices-lifecycle-performedAt" formControlName="performedAt" />
        </label>

        <label class="devices-lifecycle-event-form__full">
          {{ titleLabel }}
          <input type="text" formControlName="title" data-modal-primary-focus="true" />
        </label>

        <label class="devices-lifecycle-event-form__full">
          {{ notesLabel }}
          <textarea formControlName="notes"></textarea>
        </label>

        <label class="devices-lifecycle-event-form__full">
          {{ attachFilesLabel }}
          <input
            type="file"
            multiple
            accept="image/*,.txt,.pdf,.doc,.docx,.xls,.xlsx"
            (change)="filesSelected.emit($event)"
          />
        </label>
      </div>

      <div class="devices-lifecycle-event-form__actions">
        <button type="submit" [disabled]="form.invalid || isSubmitting">
          {{ isSubmitting ? submittingLabel : submitLabel }}
        </button>
      </div>
      @if (selectedFilesCount > 0) {
        <div class="devices-lifecycle-event-form__selected">
          {{ selectedFilesLabel }} {{ selectedFilesCount }}
        </div>
      }
    </form>
  `,
  styles: [`
    .devices-lifecycle-event-form__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 10px;
    }

    .devices-lifecycle-event-form__full {
      grid-column: 1 / -1;
    }

    label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 12px;
      color: #334155;
      font-weight: 600;
    }

    input,
    textarea,
    select {
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 8px 10px;
      color: #0f172a;
      background: #ffffff;
      font: inherit;
    }

    textarea {
      min-height: 72px;
      resize: vertical;
    }

    .devices-lifecycle-event-form__actions {
      margin-top: 4px;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    button {
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #ffffff;
      color: #0f172a;
      padding: 8px 12px;
      font-weight: 600;
      cursor: pointer;
    }

    button:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .devices-lifecycle-event-form__selected {
      margin-top: 8px;
      font-size: 12px;
      color: #0f766e;
      font-weight: 600;
    }
  `],
})
export class DevicesLifecycleEventFormComponent {
  @Input({ required: true }) public form!: FormGroup;
  @Input() public lifecycleTypes: readonly string[] = [];
  @Input() public isSubmitting = false;
  @Input() public selectedFilesCount = 0;

  @Input() public eventTypeLabel = 'Event Type';
  @Input() public performedAtLabel = 'Performed At';
  @Input() public titleLabel = 'Title';
  @Input() public notesLabel = 'Notes';
  @Input() public attachFilesLabel = 'Attach files';
  @Input() public submitLabel = 'Add event';
  @Input() public submittingLabel = 'Saving...';
  @Input() public selectedFilesLabel = 'Selected files:';

  @Output() public readonly submitForm = new EventEmitter<void>();
  @Output() public readonly filesSelected = new EventEmitter<Event>();
}
